import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Users } from 'lucide-react';
import DatePicker from 'react-datepicker';
import { addDays } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

export default function BookingWidget() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // State untuk menyimpan pilihan tamu
  const [checkin, setCheckin] = useState(new Date());
  const [checkout, setCheckout] = useState(addDays(new Date(), 1));
  const [guests, setGuests] = useState(1);

  // Fungsi untuk mengarahkan ke WhatsApp dengan terjemahan otomatis admin
  const handleCheckAvailabilityWA = () => {
    const phoneNumber = "6281378575758"; // Nomor admin Ndalem Mataram

    // Fungsi format tanggal yang mendukung pilihan bahasa
    const formatDate = (date, locale) => {
      return date.toLocaleDateString(locale, {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    };

    // Tanggal untuk Tamu (Sesuai bahasa yang aktif)
    const guestLocale = lang === 'id' ? 'id-ID' : (lang === 'zh' ? 'zh-CN' : 'en-US');
    const inDateGuest = formatDate(checkin, guestLocale);
    const outDateGuest = formatDate(checkout, guestLocale);

    // Tanggal untuk Admin (Selalu Bahasa Indonesia)
    const inDateAdmin = formatDate(checkin, 'id-ID');
    const outDateAdmin = formatDate(checkout, 'id-ID');

    // Catatan rahasia/otomatis untuk admin (hanya muncul jika bahasa asing)
    const adminCopy = `\n\n====================\n*Pesan Otomatis untuk Admin:*\nTamu di atas (berbahasa asing) menanyakan ketersediaan kamar untuk:\n*Check-in:* ${inDateAdmin}\n*Check-out:* ${outDateAdmin}\n*Jumlah Tamu:* ${guests} Orang`;

    let message = "";
    if (lang === 'id') {
      message = `Halo Ndalem Mataram,\n\nSaya berencana untuk menginap dan ingin menanyakan ketersediaan kamar dengan rincian jadwal berikut:\n\n*Check-in:* ${inDateAdmin}\n*Check-out:* ${outDateAdmin}\n*Jumlah Tamu:* ${guests} Orang\n\nMohon informasinya mengenai pilihan kamar yang masih tersedia. Terima kasih.`;
    } else if (lang === 'zh') {
      message = `你好 Ndalem Mataram，\n\n我计划在此住宿，想咨询以下日期的客房空房情况：\n\n*入住:* ${inDateGuest}\n*退房:* ${outDateGuest}\n*人数:* ${guests} 人\n\n请告知还有哪些房型可供选择。谢谢。` + adminCopy;
    } else {
      message = `Hello Ndalem Mataram,\n\nI am planning a stay and would like to inquire about room availability for the following dates:\n\n*Check-in:* ${inDateGuest}\n*Check-out:* ${outDateGuest}\n*Guests:* ${guests} Person(s)\n\nCould you please let me know what room options are still available? Thank you.` + adminCopy;
    }

    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-4 md:p-6 border border-gray-100 relative z-20 mx-4 lg:mx-0">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        
        {/* Input Check-in */}
        <div className="flex flex-col">
          <label className="text-xs font-sans font-semibold text-amber-800 uppercase tracking-widest mb-2 px-1">
            {t('hero.checkin', { defaultValue: lang === 'id' ? 'CHECK-IN' : 'CHECK-IN' })}
          </label>
          <div className="relative border border-gray-200 rounded-xl px-4 py-3 hover:border-amber-500 transition-colors bg-gray-50/50">
            <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-600 pointer-events-none" />
            <DatePicker
              selected={checkin}
              onChange={(date) => {
                setCheckin(date);
                if (date >= checkout) setCheckout(addDays(date, 1));
              }}
              minDate={new Date()}
              dateFormat="dd MMM yyyy"
              className="w-full pl-8 bg-transparent text-sm font-sans font-medium text-dark focus:outline-none cursor-pointer"
            />
          </div>
        </div>

        {/* Input Check-out */}
        <div className="flex flex-col">
          <label className="text-xs font-sans font-semibold text-amber-800 uppercase tracking-widest mb-2 px-1">
            {t('hero.checkout', { defaultValue: lang === 'id' ? 'CHECK-OUT' : 'CHECK-OUT' })}
          </label>
          <div className="relative border border-gray-200 rounded-xl px-4 py-3 hover:border-amber-500 transition-colors bg-gray-50/50">
            <Calendar size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-600 pointer-events-none" />
            <DatePicker
              selected={checkout}
              onChange={(date) => setCheckout(date)}
              minDate={addDays(checkin, 1)}
              dateFormat="dd MMM yyyy"
              className="w-full pl-8 bg-transparent text-sm font-sans font-medium text-dark focus:outline-none cursor-pointer"
            />
          </div>
        </div>

        {/* Input Tamu */}
        <div className="flex flex-col">
          <label className="text-xs font-sans font-semibold text-amber-800 uppercase tracking-widest mb-2 px-1">
            {t('hero.guests', { defaultValue: lang === 'id' ? 'TAMU' : 'GUESTS' })}
          </label>
          <div className="relative border border-gray-200 rounded-xl px-4 py-3 hover:border-amber-500 transition-colors bg-gray-50/50">
            <Users size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-amber-600 pointer-events-none" />
            <select
              value={guests}
              onChange={(e) => setGuests(Number(e.target.value))}
              className="w-full pl-8 bg-transparent text-sm font-sans font-medium text-dark focus:outline-none cursor-pointer appearance-none"
            >
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <option key={num} value={num}>
                  {num} {lang === 'id' ? 'Tamu' : lang === 'zh' ? '人' : 'Guest'}{num > 1 && lang === 'en' ? 's' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tombol CTA WhatsApp */}
        <button
          onClick={handleCheckAvailabilityWA}
          className="w-full h-[50px] bg-amber-800 hover:bg-amber-700 text-white rounded-xl font-sans font-semibold shadow-lg hover:shadow-amber-900/30 transition-all duration-300 flex items-center justify-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
            <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 0 0-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z"/>
          </svg>
          {t('hero.check_availability', { defaultValue: lang === 'id' ? 'Cek Ketersediaan' : 'Check Availability' })}
        </button>

      </div>
    </div>
  );
}